import { memo, useEffect, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { LexIcon } from "../../../../assets/icons/lex";
import useIsScrolled from "../../../hooks/useIsScrolled";
import { getLegislationById } from "../../../services";
import { getQueryValue } from "../../../store/selectors";
import {
  getHighlightedExcerpts,
  handleClickNote,
  handleInternalSpaNavigation,
  parseChapter,
  printLegislationContent,
  processDocumentText,
} from "../../../utils";
import { saveDocumentWithNotify } from "../../../utils/_handleSaveDocument";
import { Pagewrapper } from "../../components";
import { PDLTypography } from "../../components/dna";
import LexLoading from "../../components/global/_lexLoading";
import NotesModal from "../../components/global/_notesModal";
import LegislationDetailsHeaderActions from "./components/LegislationDetailsHeaderActions";
import "./index.scss";

const HtmlContent = memo(({ html }) => (
  <div className="content-text" dangerouslySetInnerHTML={{ __html: html }} />
));

const LegislationDetails = (props) => {
  const { id } = useParams();

  const [legislation, setLegislation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hideRevoked, setHideRevoked] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const isScrolled = useIsScrolled();

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteContent, setNoteContent] = useState("");
  const [noteTitle, setNoteTitle] = useState("");
  const [modalType, setModalType] = useState("note");
  const [articleId, setArticleId] = useState("");
  const [articleUrl, setArticleUrl] = useState("");

  const dispatch = useDispatch();

  const query = useSelector(getQueryValue);

  const handlePrint = () => {
    printLegislationContent({
      legislation,
      hideRevoked,
    });
  };

  const handleOpenFindBar = () => {
    window.dispatchEvent(new CustomEvent("pdl:openFindBar"));
  };

  const handleSaveDocument = () =>
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: legislation?._id ?? "",
      id: legislation?.id,
      tipo: "Legislacao",
      url: window.location.href,
    });

  const processedText = useMemo(() => {
    if (!legislation?.texto) return "";

    const nicknames =
      legislation?.apelidos?.filter((name) => name !== legislation.nome) || [];

    return processDocumentText({
      htmlText: `${
        nicknames ? `<p class="nicknames"> ${nicknames.join(" - ")} </p>` : ""
      } ${legislation.texto}`,
      hideRevoked,
    });
  }, [
    legislation?.texto,
    legislation?.apelidos,
    legislation?.nome,
    hideRevoked,
  ]);

  const mainHtml = useMemo(() => {
    if (!processedText) return "";
    return query
      ? getHighlightedExcerpts(
          processedText,
          query,
          processedText.length,
          false,
        )
      : processedText;
  }, [processedText, query]);

  const processedArticles = useMemo(() => {
    if (!legislation?.artigos?.length) return [];

    return legislation.artigos.map((art, index) => {
      const processed = processDocumentText({
        htmlText: art.texto,
        hideRevoked,
      });

      const highlighted = query
        ? getHighlightedExcerpts(processed, query, processed.length, false)
        : processed;

      return {
        ...art,
        _index: index,
        _highlighted: highlighted,
      };
    });
  }, [legislation?.artigos, hideRevoked, query]);

  useEffect(() => {
    const loadLegislation = async () => {
      setShowNoteModal(false);
      try {
        setLoading(true);
        setError(null);

        const response = await getLegislationById(id);

        setLegislation(response);
      } catch (err) {
        const status = err?.response?.status;

        setError(
          status === 404
            ? "Estamos trabalhando para disponibilizá-lo em breve."
            : err?.response?.data?.message ||
                "Não foi possível encontrar a legislação",
        );
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadLegislation();
    }
  }, [id]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      const rawTarget = event.target;

      const target =
        rawTarget.closest?.("[data-nota-link], .nota-super, a") || rawTarget;
      if (showNoteModal) return;

      if (target.classList?.contains("nota-super")) {
        handleClickNote(event);
        return;
      }

      if (target.hasAttribute("data-nota-link")) {
        event.preventDefault();

        const noteKey = target.getAttribute("data-nota-link");
        if (!noteKey) return;

        const note = legislation?.notas?.[noteKey];
        if (!note) return;

        let titleClicked = "";

        const tag = target.tagName.toLowerCase();

        if (
          tag === "a" ||
          tag === "strong" ||
          tag === "p" ||
          /^h[1-6]$/.test(tag)
        ) {
          titleClicked = target.textContent?.trim();
        }

        if (!titleClicked) {
          titleClicked = note?.descricao || `Nota ${noteKey}`;
        }

        const texto = note?.["texto:"] || note?.texto || "";
        setModalType("note");
        setNoteTitle(titleClicked);
        setNoteContent(processDocumentText({ htmlText: texto, hideRevoked }));
        setShowNoteModal(true);
      }

      if (target.tagName?.toLowerCase() === "a") {
        const href = target.getAttribute("href") || "";

        const match = href.match(/(art\d+)/i);

        if (match) {
          event.preventDefault();

          const articlePart = match[1];

          let cleaned = href.replace(/^\/?legislacao\//i, "");
          cleaned = cleaned.split("#")[0];

          if (!cleaned.toLowerCase().endsWith(articlePart.toLowerCase())) {
            cleaned = cleaned.replace(/art\d+/i, "") + articlePart;
          }

          setArticleId(cleaned);
          setArticleUrl(href);
          setModalType("legislation");
          setShowNoteModal(true);

          return;
        } else {
          if (handleInternalSpaNavigation(href, event)) {
            return;
          }
        }
      }
    };

    document.addEventListener("click", handleGlobalClick);
    return () => document.removeEventListener("click", handleGlobalClick);
  }, [legislation, showNoteModal, setNoteContent, hideRevoked]);

  useEffect(() => {
    if (loading) return;
    if (!legislation) return;

    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.replace("#", "");

    const timer = setTimeout(() => {
      const target = document.getElementById(id);

      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        let retries = 0;
        const retryInterval = setInterval(() => {
          const el = document.getElementById(id);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            clearInterval(retryInterval);
          }
          retries++;
          if (retries > 20) {
            clearInterval(retryInterval);
          }
        }, 200);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [loading, legislation]);

  if (loading) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="content-loading">
                <LexLoading customText="Buscando legislação..." />
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  if (error) {
    return (
      <Pagewrapper {...props}>
        <Container>
          <Row>
            <Col>
              <div className="error-section text-center">
                <LexIcon />
                <PDLTypography size="xl" variant="primary" weight="medium">
                  Este documento ainda não está disponível.
                </PDLTypography>
                <p className="text-muted">{error}</p>
                <p className="text-muted">
                  Se for urgente, fale com a gente. Teremos prazer em ajudar.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </Pagewrapper>
    );
  }

  return (
    <Pagewrapper {...props} showHeader headerClass="details-sticky">
      <Container>
        <Row>
          <Col>
            <LegislationDetailsHeaderActions
              title={legislation?.nome || "Legislação"}
              isScrolled={isScrolled}
              hideRevoked={hideRevoked}
              isSavingDocument={isSavingDocument}
              onToggleHideRevoked={() => setHideRevoked((prev) => !prev)}
              onOpenFindBar={handleOpenFindBar}
              onSaveDocument={handleSaveDocument}
              onPrint={handlePrint}
            />
            <div id="details-content" className="mt-4">
              <div className="legislation content-box">
                {legislation?.texto ? (
                  <HtmlContent html={mainHtml} />
                ) : (
                  <p className="text-muted">Conteúdo não disponível.</p>
                )}
              </div>

              <div className="content-box">
                {processedArticles.length > 0 &&
                  processedArticles.map((art) => {
                    const prev = processedArticles[art._index - 1];

                    const showBook = !prev || prev.livro !== art.livro;
                    const showTitle = !prev || prev.titulo !== art.titulo;
                    const showChapter = !prev || prev.capitulo !== art.capitulo;
                    const showSecao = !prev || prev.secao !== art.secao;

                    return (
                      <div key={art._index} className="article-item">
                        {showBook && art.livro && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.livro.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        {showTitle && art.titulo && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.titulo.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        {showChapter &&
                          art.capitulo &&
                          (() => {
                            const { title, notes } = parseChapter(
                              art.capitulo,
                              query,
                            );

                            return (
                              <div className="article-title-box">
                                <p
                                  className="article-chapter"
                                  dangerouslySetInnerHTML={{
                                    __html: String(title)?.replace(
                                      " - ",
                                      "<br/>",
                                    ),
                                  }}
                                />
                                {notes && (
                                  <div
                                    className="content-text"
                                    dangerouslySetInnerHTML={{ __html: notes }}
                                  />
                                )}
                              </div>
                            );
                          })()}

                        {showSecao && art.secao && (
                          <div className="article-title-box">
                            <p
                              className="article-chapter"
                              dangerouslySetInnerHTML={{
                                __html: art.secao.replace(" - ", "<br/>"),
                              }}
                            />
                          </div>
                        )}

                        <HtmlContent html={art._highlighted} />
                      </div>
                    );
                  })}
              </div>
            </div>

            <NotesModal
              noteContent={noteContent}
              noteTitle={noteTitle}
              setShowNoteModal={setShowNoteModal}
              showNoteModal={showNoteModal}
              type={modalType}
              articleId={articleId}
              url={articleUrl}
            />
          </Col>
        </Row>
      </Container>
    </Pagewrapper>
  );
};

export default LegislationDetails;
